import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

import { useCollectionsSearch } from 'gql/api/queries/collections-search.generated';
import { useMomentsSearch } from 'gql/api/queries/moments-search.generated';
import { useUsersSearch } from 'gql/api/queries/users-search.generated';
import { useWorldsSearch } from 'gql/api/queries/worlds-search.generated';
import { getPromiseValueOrFallback } from 'utils/helpers';

import type { GlobalSearchResults } from 'types/Search';

type GlobalSearchVariables = {
  searchTerm: string;
};

export default function useGlobalSearch(
  variables: GlobalSearchVariables,
  options: Pick<
    UseQueryOptions<GlobalSearchResults, Error>,
    'enabled' | 'placeholderData'
  > = {}
) {
  return useQuery({
    ...options,
    queryKey: useGlobalSearch.getKey(variables),
    queryFn: () => getGlobalSearch(variables),
  });
}

useGlobalSearch.getKey = (variables: GlobalSearchVariables) => [
  'GlobalSearch',
  variables,
];

async function getGlobalSearch(
  props: GlobalSearchVariables
): Promise<GlobalSearchResults> {
  const { searchTerm } = props;

  /**
   * Our search results are currently returned from the API as separate queries.
   * By using Promise.allSettled, we can fetch all in parallel, and handle scenarios where one fails.
   * */
  const [
    worldPromiseResult,
    momentPromiseResult,
    usersPromiseResult,
    collectionsPromiseResult,
    // nftPromiseResult,
  ] = await Promise.allSettled([
    getWorldsSearch({ searchTerm }),
    getMomentsSearch({ searchTerm }),
    getUsersSearch({ searchTerm, page: 0, perPage: 3 }),
    getCollectionsSearch({ searchTerm, page: 0, perPage: 3 }),
    // getNftsSearch({ searchTerm, page: 0, perPage: 3 }),
  ]);

  /** Parse promise results, falling back to null if any were rejected  */
  // const nftResults = getPromiseValueOrFallback(nftPromiseResult, null);
  const worldResults = getPromiseValueOrFallback(worldPromiseResult, null);
  const momentResults = getPromiseValueOrFallback(momentPromiseResult, null);
  const usersResults = getPromiseValueOrFallback(usersPromiseResult, null);
  const collectionsResults = getPromiseValueOrFallback(
    collectionsPromiseResult,
    null
  );
  const artworks = {
    items: [],
  };
  const worlds = {
    items: worldResults ? worldResults : [],
  };
  const moments = {
    items: momentResults ? momentResults : [],
  };
  const users = {
    items: usersResults ? usersResults : [],
  };
  const collections = {
    items: collectionsResults ? collectionsResults : [],
  };

  /** Return data in a normalized format */
  return {
    artworks,
    collections,
    users,
    worlds,
    moments,
  };
}

export async function getUsersSearch(
  variables: GlobalSearchVariables & {
    page: number;
    perPage: number;
  }
) {
  const fetchUsers = useUsersSearch.fetcher(variables);
  const data = await fetchUsers();

  if (!data) return [];

  return data.search.items;
}

async function getWorldsSearch(variables: GlobalSearchVariables) {
  const fetchWorlds = useWorldsSearch.fetcher(variables);
  const data = await fetchWorlds();

  if (!data) return [];

  return data.search.worlds;
}

async function getMomentsSearch(variables: GlobalSearchVariables) {
  const fetchWorlds = useMomentsSearch.fetcher(variables);
  const data = await fetchWorlds();

  if (!data) return [];

  return data.momentsSearch.items;
}

async function getCollectionsSearch(
  variables: GlobalSearchVariables & {
    page: number;
    perPage: number;
  }
) {
  const fetchCollections = useCollectionsSearch.fetcher(variables);
  const data = await fetchCollections();

  if (!data) return [];

  return data.search.items;
}

// async function getNftsSearch(
//   variables: GlobalSearchVariables & {
//     page: number;
//     perPage: number;
//   }
// ) {
//   const fetchNfts = useNftsSearchCached.fetcher({
//     ...variables,
//     sortOrder: 'MINT_DATE_DESC',
//   });
//   const data = await fetchNfts();

//   if (!data) return [];

//   return data.search.items;
// }
